import { useState } from 'react';
import { IconChecks, IconMaximize, IconMessage, IconSearch, IconX } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { useToast } from '@hana/ui';
import './i18n';
import { AP_ICON, AP_ROWS, AP_SRC, type ApRow } from './data';
import s from './home.module.css';
import a from './approvals.module.css';

// Mint-Home (1536) · N-04c: khối Duyệt chờ tôi (vai sale) — theo code board: ap.kinds / seats / rows, cap 4 (xem thêm = mở rộng).
const KINDS = [['all', 'hn.ap.k.all'], ['money', 'hn.ap.k.money'], ['hr', 'hn.ap.k.hr'], ['other', 'hn.ap.k.other']] as const;
const SEAT_ME = 'SALE-MGR';
const SRC_ZONE: Record<string, string> = { money: '/ezy/ar', hr: '/mee', other: '/ezy/jobs' };
type Dec = 'ok' | 'no' | 'ask';
const DEC_K: Record<Dec, string> = { ok: 'hn.ap.d.ok', no: 'hn.ap.d.no', ask: 'hn.ap.d.ask' };

function seatOf(r: ApRow): string {
  const ms = (r.steps[r.cur]?.s ?? '').match(/[A-Z]{2,}-[A-Z]{2,}/g) ?? [SEAT_ME];
  const last = ms[ms.length - 1] ?? SEAT_ME;
  return last === 'BOD-DEP' ? 'BOD-DDIR' : last;
}
const norm = (v: string) => v.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const noBulk = (r: ApRow) => r.flags.some((f) => f.k === 'Cần mở từng phiếu' || f.k === 'Bỏ qua Trưởng KD');

export function Approvals({ empty }: { empty: boolean }) {
  const t = useT();
  const toast = useToast();
  const tip = (m: string) => toast({ msg: m });
  const [kind, setKind] = useState<string>('all');
  const [seat, setSeat] = useState('all');
  const [type, setType] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState<number | null>(null);
  const [pick, setPick] = useState<Record<number, boolean>>({});
  const [dec, setDec] = useState<Record<number, Dec>>({});
  const all = empty ? [] : AP_ROWS;
  const pendAll = all.filter((r) => !dec[r.id]);
  const seats = [...new Set(all.map(seatOf))];
  let f = all.filter((r) => kind === 'all' || r.cat === kind);
  if (seat !== 'all') f = f.filter((r) => seatOf(r) === seat);
  if (type) f = f.filter((r) => r.ik === type);
  if (q) f = f.filter((r) => norm(`${r.kind} ${r.title} ${r.by}`).includes(norm(q)));
  const pend = f.filter((r) => !dec[r.id]);
  const picked = pend.filter((r) => pick[r.id]);
  const pickedOk = picked.filter((r) => !noBulk(r));
  const cap = open ? 999 : 4;
  const rows = f.slice(0, cap);
  const kinds: [string, string][] = []; all.forEach((r) => { if (!kinds.some((k) => k[0] === r.ik)) kinds.push([r.ik, r.kind]); });
  const cnt = (p: (r: ApRow) => boolean) => pendAll.filter(p).length;
  const oldest = pendAll.length ? Math.max(0, 22 - Math.min(...pendAll.map((r) => parseInt(r.when, 10) || 22))) : 0;
  const allPicked = pend.length > 0 && pend.every((r) => pick[r.id]);
  const decide = (r: ApRow, d: Dec) => { setDec((p) => ({ ...p, [r.id]: d })); setPick((p) => ({ ...p, [r.id]: false })); };
  return (
    <section className={s.my} aria-label={t('hn.ap.title')}>
      <div className={s.myH}>
        <h2 title={t('hn.ap.seat')}>{t('hn.ap.title')}</h2>
        <span className={s.sum}>{pendAll.length ? t('hn.ap.sumOld', { n: pendAll.length, d: oldest }) : t('hn.ap.sum', { n: pendAll.length })}</span>
        <span className={s.grow} />
        {seats.length > 1 && (
          <div className={s.seg} role="group" aria-label={t('hn.ap.bySeat')}>
            {([['all', t('hn.ap.allSeats')], ...seats.map((x) => [x, `[${x}]${x === 'BOD-DDIR' ? t('hn.ap.power') : ''}`] as [string, string])] as [string, string][]).map(([k, l]) => (
              <button key={k} type="button" aria-pressed={seat === k} onClick={() => { setSeat(k); setPick({}); }}>{l} · {k === 'all' ? pendAll.length : cnt((r) => seatOf(r) === k)}</button>
            ))}
          </div>
        )}
        {!open && <button type="button" className={s.ib} aria-label={t('hn.ap.expand')} title={t('home.expand')} onClick={() => setOpen(true)}><IconMaximize size={15} stroke={2} aria-hidden="true" /></button>}
      </div>
      <div className={s.tools}>
        {KINDS.map(([k, l]) => (
          <button key={k} type="button" className={`${s.bt} ${kind === k ? s.pri : ''}`} aria-pressed={kind === k} onClick={() => { setKind(k); setPick({}); }}>{t(l)} <span className={a.n}>{cnt((r) => k === 'all' || r.cat === k)}</span></button>
        ))}
        <select className={a.sel} aria-label={t('hn.ap.kindL')} value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">{t('hn.ap.allKinds')}</option>
          {kinds.map(([v, k]) => <option key={v} value={v}>{k} · {cnt((r) => r.ik === v)}</option>)}
        </select>
        <label className={`${s.find} ${a.fq}`}><IconSearch size={14} stroke={2.2} aria-hidden="true" /><input aria-label={t('hn.ap.findL')} placeholder={t('hn.ap.findPh')} value={q} onChange={(e) => setQ(e.target.value)} /></label>
      </div>
      {picked.length > 0 && (
        <div className={a.bulk} role="region" aria-label={t('hn.ap.bulk')}>
          <div className={a.bulkTx}><b>{t('hn.ap.picked', { n: picked.length })}</b> <span>· {pickedOk.length < picked.length ? t('hn.ap.noBulk', { n: picked.length - pickedOk.length }) : t('hn.ap.nextStep')}</span></div>
          <button type="button" className={`${s.bt} ${a.ghost}`} onClick={() => setPick({})}><IconX size={16} stroke={1.8} aria-hidden="true" />{t('hn.ap.unpick')}</button>
          <button type="button" className={`${s.bt} ${a.out}`} onClick={() => tip(t('home.later'))}><IconX size={16} stroke={1.8} aria-hidden="true" />{t('hn.ap.rejectN', { n: picked.length })}</button>
          <button type="button" className={`${s.bt} ${a.ok}`} disabled={!pickedOk.length} onClick={() => { setDec((p) => { const n = { ...p }; pickedOk.forEach((r) => { n[r.id] = 'ok'; }); return n; }); setPick({}); tip(t('hn.ap.agreedN', { n: pickedOk.length })); }}><IconChecks size={16} stroke={1.8} aria-hidden="true" />{t('hn.ap.agreeN', { n: pickedOk.length })}</button>
        </div>
      )}
      <div className={a.all}><input type="checkbox" aria-label={t('hn.ap.pickAllL')} checked={allPicked} onChange={() => setPick(allPicked ? {} : Object.fromEntries(pend.map((r) => [r.id, true])))} /><span>{t('hn.ap.pickAll')}</span></div>
      {!rows.length && <div className={s.none}>{all.length ? t('hn.ap.noMatch') : t('hn.ap.none')}</div>}
      {rows.map((r) => {
        const d = dec[r.id];
        const isSel = sel === r.id;
        return (
          <div key={r.id}>
            <div className={`${s.row} ${a.ar} ${isSel ? a.sel2 : ''}`} onClick={() => setSel(isSel ? null : r.id)}>
              <input type="checkbox" aria-label={t('home.pick', { t: r.kind })} checked={!!pick[r.id]} disabled={!!d} onChange={() => setPick((p) => ({ ...p, [r.id]: !p[r.id] }))} onClick={(e) => e.stopPropagation()} />
              <span className={a.ic} aria-hidden="true" style={{ opacity: d ? 0.6 : 1 }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d={AP_ICON[r.ik] ?? AP_ICON.adm} /></svg></span>
              <div className={s.rb} style={{ opacity: d ? 0.6 : 1 }}>
                <div className={a.k}><b>{r.kind}</b>{r.flags.map((x) => <span key={x.k} className={`${a.flag} ${x.c === 'warn' ? a.warn : ''}`} title={x.tip}>{x.k}</span>)}</div>
                <div className={a.sub}><a href={SRC_ZONE[r.cat] ?? AP_SRC[r.ik]} title={t('hn.ap.openSrc')} onClick={(e) => e.stopPropagation()}>{r.title} ›</a> · {t('hn.ap.by')}: {r.by} · {r.when}</div>
              </div>
              <div className={a.steps} style={{ opacity: d ? 0.6 : 1 }}>
                <div className={a.bars}>{r.steps.map((p, i) => <i key={p.k} title={`${p.k} · ${p.s}`} className={p.d === 'd' || (d === 'ok' && i === r.cur) ? a.sd : i === r.cur ? a.sc : undefined} />)}</div>
                <div className={a.stk}>{t('hn.ap.step', { i: r.cur + 1, n: r.steps.length })} · {r.steps[r.cur]?.s}</div>
              </div>
              <div className={s.ra}>
                {d ? <span className={`${a.dec} ${d === 'no' ? a.no : d === 'ok' ? a.yes : ''}`}>{t(DEC_K[d])}</span> : (<>
                  <button type="button" className={`${s.bt} ${a.lnk}`} onClick={(e) => { e.stopPropagation(); tip(t('home.later')); }}><IconMessage size={16} stroke={1.8} aria-hidden="true" />{t('hn.ap.ask')}</button>
                  <button type="button" className={`${s.bt} ${a.out}`} onClick={(e) => { e.stopPropagation(); tip(t('home.later')); }}><IconX size={16} stroke={1.8} aria-hidden="true" />{t('hn.reject')}</button>
                  <button type="button" className={`${s.bt} ${a.ok}`} onClick={(e) => { e.stopPropagation(); decide(r, 'ok'); tip(t('hn.agreed', { k: r.kind })); }}><IconChecks size={16} stroke={1.8} aria-hidden="true" />{t('hn.agree')}</button>
                </>)}
              </div>
            </div>
            {isSel && (
              <div className={a.det}>
                <dl>{[[t('hn.ap.by2'), r.by], [t('hn.ap.sentAt'), `${r.when}/2026`], [t('home.note'), r.note]].map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
              </div>
            )}
          </div>
        );
      })}
      {!open && f.length > cap && <button type="button" className={s.more} onClick={() => setOpen(true)}>{t('hn.ap.seeMore', { n: f.length - cap })}</button>}
    </section>
  );
}
